import { skipToken } from '@apollo/client/react'
import { CircularProgress, IconButton } from '@mui/material'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useAsyncFn } from 'react-use'
import FluentCheckmark16Filled from '~icons/fluent/checkmark-16-filled'
import FluentLinkMultiple16Filled from '~icons/fluent/link-multiple-16-filled'
import IcRoundIosShare from '~icons/ic/round-ios-share'
import MaterialSymbolsDownload from '~icons/material-symbols/download'
import {
  downloadFile,
  getImageDownloadFileExt,
  getImageDownloadFilename,
} from 'src/components/ArtworkDetail/utils'
import { getArtworkShareLink } from 'src/components/SocialShare/SocialShare'
import { useCopy } from 'src/hooks/useCopy'
import { useGetArtwork } from 'src/services/apollo/artwork'
import type { ArtworkDetailFragment } from 'src/services/apollo/generated/graphql'
import { isAnimatedArtwork } from 'src/utils/artwork'
import { useIsMobile } from 'src/utils/hooks/useBreakpoint'
import { getMediaUrl } from 'src/utils/media'
import { formatParamURL } from 'src/utils/qs'
import { track } from 'src/utils/tracker'
import type { ArtworkForShare } from './ArtworkShareContent'
import { ArtworkSharePopover } from './ArtworkShareDialog'

/**
 * Either artworkId or artwork needs to be provided
 * @param props
 * @returns
 */
export default function ArtworkDetailActions(props: {
  artwork?: ArtworkForShare & Pick<ArtworkDetailFragment, 'mediaId'>
  artworkId?: string
  trackingArgs?: Record<string, any>
  mutateArtwork?: () => void
}) {
  const { trackingArgs } = props
  const { t } = useTranslation(['artwork'])
  const [copy, copied] = useCopy()
  const { data: artworkData } = useGetArtwork(props.artworkId ?? skipToken)
  const artwork = artworkData ?? props.artwork

  const isAnimated = isAnimatedArtwork(artwork)
  const videoMediaUrl = getMediaUrl(artwork?.videoMediaId, 'orig')

  const [{ loading: loadingDownload }, downloadArtwork] =
    useAsyncFn(async () => {
      if (!artwork?.media) return
      track('share_image_dl_click', trackingArgs)

      const imageExt = getImageDownloadFileExt({
        artwork,
        media: artwork.media,
      })
      const imageFileName = `${getImageDownloadFilename({ artwork })}.${imageExt}`

      const url = videoMediaUrl || getMediaUrl(artwork.mediaId, 'orig')
      if (url) {
        try {
          await downloadFile(url, imageFileName, { skipConversion: isAnimated })
          track('share_image_dl_dialog_show', trackingArgs)
        } catch (err: any) {
          toast.error(
            t('artwork:share.watermark-dialog.notice.failed-to-download') +
              ': ' +
              err.message,
          )
        }
      }
    }, [artwork])

  const isMobile = useIsMobile()

  if (!artwork) return null

  return (
    <>
      <IconButton
        onClick={() => {
          track('copy_link_click', {
            ...trackingArgs,
            location: 'artwork-detail',
          })
          copy(
            formatParamURL(getArtworkShareLink(artwork.id), {
              utm_source: 'copy_web',
            }),
          )
        }}
      >
        {copied ? (
          <FluentCheckmark16Filled className="size-4 text-green-500" />
        ) : (
          <FluentLinkMultiple16Filled className="size-4" />
        )}
      </IconButton>
      {!isMobile && (
        <IconButton disabled={loadingDownload} onClick={downloadArtwork}>
          {loadingDownload ? (
            <CircularProgress size={20} className="text-primary" />
          ) : (
            <MaterialSymbolsDownload className="size-5" />
          )}
        </IconButton>
      )}
      <ShareButton artwork={artwork} trackingArgs={trackingArgs} />
    </>
  )
}
function ShareButton(props: {
  artwork: ArtworkForShare
  trackingArgs?: Record<string, any>
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  return (
    <>
      <IconButton
        ref={triggerRef}
        onClick={() => {
          track('share_btn_click', props.trackingArgs)
          setOpen(true)
        }}
      >
        <IcRoundIosShare className="size-4" />
      </IconButton>
      <ArtworkSharePopover
        open={open}
        onOpenChange={setOpen}
        triggerRef={triggerRef}
        artwork={props.artwork}
        trackingArgs={props.trackingArgs}
      />
    </>
  )
}
